/**
 * @fileoverview The canvas kit the share pictures and the season's picture
 * share: the faces (SERIF, BODY, ensureFonts), a photograph cropped to fill
 * (drawCover), rounded shapes, wrapped lines, the days between two dates, the
 * Grow mark, and a handle with its place's mark. Moved out of
 * plant-list/lib/shareFrames (batch 18, A110); nothing in it changed.
 *
 * Where: the frames' drawing (features/share/frames) and, through
 * plant-list/lib/shareFrames, the season review's picture
 * (season/lib/seasonFrame).
 * Settings: SHARE_PALE_INK_LUMINANCE (config/tunables/plantList).
 *
 * @module canvasKit
 */

import { coverPlacement, type CropFocus } from '@/shared/lib/photoFit';
import { SOCIAL_MARKS, type LinkKey } from '@/shared/lib/socialLinks';
import { SHARE_PALE_INK_LUMINANCE } from '@/config/tunables/plantList';

/**
 * The mark: a leaf and the word Grow, first line of the text block.
 *
 * What survives a picture being saved and posted anywhere is a mark people
 * recognise, not an address. A URL on a photograph is not a link: nobody
 * types it, it drives nothing measurable, and it reads as a stock-photo
 * watermark; the link travels with the share instead, where it can be
 * clicked. This is the same leaf the app wears. An account the Grow settings
 * list (accounts.NO_SHARE_MARK_USER_IDS) shares without it (showMark false),
 * and the words move up into its place.
 */
const GROW_LEAF = 'M20 3c-7 0-13 3-16 9-1.5 3-1 6 0 9 4-6 9-9 14-11-4 3-8 7-10 12 6 1 11-2 13-8 1-4 0-8-1-11z';
/** The word beside the leaf. */
const GROW_WORD = 'Grow';

/**
 * The faces the frame is lettered in, ready before anything is drawn.
 *
 * Canvas does not wait for a webfont the way the document does. It silently
 * falls back to the nearest system face and draws it, so a frame built in the
 * first moments after a cold load came out in Times while the same frame built
 * a second later came out in Playfair. Asking for the exact weights first
 * makes that race go away.
 */
export async function ensureFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load('600 64px "Playfair Display"'),
      document.fonts.load('700 64px "Playfair Display"'),
      document.fonts.load('400 48px "Lora"'),
      document.fonts.load('500 48px "Lora"'),
    ]);
  } catch {
    /* A face that will not load is a worse-looking frame, not a failed share. */
  }
}

/** The face of the Grow mark, the plant's name and the before-and-after span. */
export const SERIF = '"Playfair Display", Georgia, serif';
/** The face of everything else printed. */
export const BODY = '"Lora", Georgia, serif';

/**
 * A pill (fully rounded ends), for the before-and-after band, as a path.
 *
 * @param ctx - The frame's canvas.
 * @param x - Its left edge.
 * @param y - Its top edge.
 * @param w - Its width.
 * @param h - Its height.
 */
export function pillPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const r = Math.min(h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + r, y + h);
  ctx.arc(x + r, y + r, r, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
}

/**
 * Draws an image to fill a rectangle without distorting it (or, when it is too
 * small to fill it, at its own size in the middle, never enlarged), cropped where
 * focus says (0..1 across and down; the middle when not given).
 *
 * @param ctx - The frame's canvas.
 * @param image - The photograph, decoded.
 * @param x - The rectangle's left edge.
 * @param y - Its top edge.
 * @param w - Its width.
 * @param h - Its height.
 * @param focus - Which part of the photograph the crop keeps.
 */
export function drawCover(
  ctx: CanvasRenderingContext2D, image: ImageBitmap,
  x: number, y: number, w: number, h: number,
  focus: CropFocus = { x: 0.5, y: 0.5 }
): void {
  // Filling the frame, zoomed by the gardener's choice; a photograph too
  // small to fill it is not enlarged but sits in the middle (B32).
  const { width, height, dx, dy } = coverPlacement(image, { width: w, height: h }, focus);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(image, x + dx, y + dy, width, height);
  ctx.restore();
}

/**
 * A rectangle with rounded corners, for the card, as a path to fill or clip.
 *
 * @param ctx - The frame's canvas.
 * @param x - Its left edge.
 * @param y - Its top edge.
 * @param w - Its width.
 * @param h - Its height.
 * @param r - The corners' radius.
 */
export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Splits text into lines that fit, breaking on spaces. Past maxLines the
 * last line ends in an ellipsis, so a cut is never silent.
 *
 * @param ctx - The canvas, set in the font the lines will be drawn in.
 * @param text - The text.
 * @param maxWidth - The width a line may take, in pixels.
 * @param maxLines - The most lines.
 */
export function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
      if (lines.length === maxLines) break;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines && words.join(' ') !== lines.join(' ')) {
    // Something was dropped, so the last line has to admit it.
    let last = lines[maxLines - 1] ?? '';
    while (last && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1).trimEnd();
    }
    lines[maxLines - 1] = `${last}…`;
  }
  return lines;
}

/**
 * Days between two dates, at whole-day resolution; never fewer than none.
 *
 * @param fromIso - The earlier date.
 * @param toIso - The later date.
 */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/**
 * The Grow mark: the leaf, then the word, on one baseline.
 *
 * @param ctx - The frame's canvas.
 * @param x - The leaf's left edge.
 * @param baseline - The word's baseline.
 * @param U - The lettering's unit.
 * @param colour - The mark's colour.
 * @returns The mark's width, in pixels.
 */
export function drawGrowMark(ctx: CanvasRenderingContext2D, x: number, baseline: number, U: number, colour: string): number {
  const leaf = Math.round(34 * U);
  ctx.save();
  ctx.translate(x, baseline - leaf + Math.round(2 * U));
  ctx.scale(leaf / 24, leaf / 24);
  ctx.fillStyle = colour;
  ctx.fill(new Path2D(GROW_LEAF));
  ctx.restore();
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `600 ${Math.round(36 * U)}px ${SERIF}`;
  ctx.fillStyle = colour;
  const gap = Math.round(8 * U);
  ctx.fillText(GROW_WORD, x + leaf + gap, baseline);
  const width = leaf + gap + ctx.measureText(GROW_WORD).width;
  ctx.restore();
  return width;
}

/**
 * Whether an ink is pale enough to need a shadow under it: white over a
 * photograph, or a card ground's ink from the Grow settings ("#RRGGBB"),
 * judged by its luminance, since the owner can make it any colour.
 *
 * @param ink - "rgba(255,…)" or "#RRGGBB".
 */
export function isPale(ink: string): boolean {
  if (ink.startsWith('rgba(255')) return true;
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(ink);
  if (!hex) return false;
  const [r, g, b] = hex.slice(1).map((part) => parseInt(part, 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > SHARE_PALE_INK_LUMINANCE;
}

/**
 * A handle, top left, with the mark of the place it lives in that place's
 * colour on a white disc (drawHandle, and the season review's picture).
 *
 * @param ctx - The frame's canvas.
 * @param handle - The handle, already tidied, and where it lives.
 * @param margin - The frame's margin.
 * @param safeTop - What the app showing a story covers at its top.
 * @param U - The lettering's unit.
 * @param ink - The handle's colour: white over a photograph, a ground's ink on a plain ground.
 */
export function drawHandleAt(
  ctx: CanvasRenderingContext2D, handle: { text: string; place: LinkKey }, margin: number, safeTop: number, U: number,
  ink = 'rgba(255,255,255,0.95)',
): void {
  const size = Math.round(38 * U);
  const baseline = safeTop + margin + size;
  const mark = SOCIAL_MARKS[handle.place];
  const iconSize = Math.round(36 * U);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = Math.round(12 * U);

  // A white disc under the mark keeps a brand colour legible over any
  // photograph, and gives TikTok's black glyph something to be black on.
  const cx = margin + iconSize / 2;
  const cy = baseline - iconSize / 2 + Math.round(2 * U);
  ctx.beginPath();
  ctx.arc(cx, cy, iconSize * 0.72, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.translate(cx - iconSize / 2, cy - iconSize / 2);
  ctx.scale(iconSize / 24, iconSize / 24);
  ctx.fillStyle = mark.color === 'currentColor' ? '#111111' : mark.color;
  ctx.fill(new Path2D(mark.path));
  ctx.restore();

  ctx.save();
  // A shadow lifts pale lettering off a photograph or a dark ground; dark ink
  // on a pale card ground needs none.
  const light = isPale(ink);
  ctx.shadowColor = light ? 'rgba(0,0,0,0.55)' : 'transparent';
  ctx.shadowBlur = light ? Math.round(12 * U) : 0;
  ctx.textAlign = 'left';
  ctx.font = `400 ${size}px ${BODY}`;
  // A long handle at a large size shrinks to stay inside the frame's margin
  // rather than running off the edge.
  const x = margin + iconSize + Math.round(14 * U);
  const room = ctx.canvas.width - margin - x;
  const wide = ctx.measureText(handle.text).width;
  if (wide > room) ctx.font = `400 ${Math.floor(size * (room / wide))}px ${BODY}`;
  ctx.fillStyle = ink;
  ctx.fillText(handle.text, x, baseline);
  ctx.restore();
}
